import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useEditMode } from "@/contexts/CmsContext";

// ── PreviewHighlighter ────────────────────────────────────────────────────
//
// Renders nothing. Watches for `?cmsPreview=<slot>` in the URL — the
// exact query parameter that `/admin/images` "View" buttons append —
// then finds the matching <img> on the current page, scrolls it into
// view, and flashes a soft ring around it so the editor immediately
// sees the image being managed.
//
// Slot lookup runs across the three stable-slot conventions used across
// the site:
//   • `[data-cms-slot="<id>"]`         — explicit legacy attribute
//   • `[data-cms-image-slot="<id>"]`   — <EditableImage> wrapper
//   • `[data-cms-scope=".."][data-cms-role="..."]` compositional
//
// A polling retry (~10s max) is required because most images on the
// site lazy-load once scrolled into range, and hero heavy pages hydrate
// their CMS overrides asynchronously.
const HIGHLIGHT_CLASS = "cms-preview-highlight";

// Inject the highlight ring stylesheet once. Uses a large soft box
// shadow + brand accent colour so it reads over both light and dark
// backgrounds without covering the image itself.
let styleInjected = false;
const injectStyle = () => {
  if (styleInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.setAttribute("data-cms-preview-highlight", "true");
  style.textContent = `
    .${HIGHLIGHT_CLASS} {
      outline: 3px solid #C16542;
      outline-offset: 4px;
      box-shadow:
        0 0 0 8px rgba(193, 101, 66, 0.18),
        0 0 40px 4px rgba(193, 101, 66, 0.55);
      transition: outline-color 0.4s ease, box-shadow 0.4s ease;
      animation: cms-preview-pulse 1.6s ease-in-out 3;
      position: relative;
      z-index: 5;
    }
    @keyframes cms-preview-pulse {
      0%, 100% { box-shadow: 0 0 0 8px rgba(193, 101, 66, 0.15), 0 0 40px 4px rgba(193, 101, 66, 0.4); }
      50%      { box-shadow: 0 0 0 14px rgba(193, 101, 66, 0.05), 0 0 60px 10px rgba(193, 101, 66, 0.7); }
    }
  `;
  document.head.appendChild(style);
  styleInjected = true;
};

// Locate the DOM node that hosts the given slot id. Prefers the
// smallest matching element so hero backdrops and gallery thumbs both
// resolve to their own <img>, never a wrapping <section>.
const findSlotElement = (slotId) => {
  if (!slotId || typeof document === "undefined") return null;
  const esc = window.CSS && window.CSS.escape ? window.CSS.escape(slotId) : slotId.replace(/"/g, '\\"');
  // Tier 1 — explicit data-cms-slot on the <img>.
  const direct = document.querySelector(`[data-cms-slot="${esc}"]`);
  if (direct) return direct;
  // Tier 1b — <EditableImage> wrapper — target the <img> inside so the
  // ring hugs the photo, not the invisible surrounding <span>.
  const wrapper = document.querySelector(`[data-cms-image-slot="${esc}"]`);
  if (wrapper) return wrapper.querySelector("img") || wrapper;
  // Tier 2 — compositional `cms.<scope>.<role>` slot: split and hunt.
  const m = slotId.match(/^cms\.(.+)\.([^.]+)$/);
  if (m) {
    const [, scope, role] = m;
    const scopeEsc = window.CSS && window.CSS.escape ? window.CSS.escape(scope) : scope;
    const roleEsc  = window.CSS && window.CSS.escape ? window.CSS.escape(role)  : role;
    // Prefer the specific <img> with matching data-cms-role that lives
    // inside the correct scope. Fallback to any element with the role.
    const nodes = document.querySelectorAll(`[data-cms-role="${roleEsc}"]`);
    for (const n of nodes) {
      if (n.closest(`[data-cms-scope="${scopeEsc}"]`)) return n;
    }
    if (nodes.length) return nodes[0];
  }
  return null;
};

const PreviewHighlighter = () => {
  const location = useLocation();
  const { openImageEditor } = useEditMode();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(location.search);
    const slot = params.get("cmsPreview");
    if (!slot) return;
    injectStyle();

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 40; // ~10s total (40 × 250ms)

    // Always hand off to the editor for the requested slot even when
    // no matching <img> lives on the page (e.g. legacy slot ids that
    // survive in the DB but are no longer rendered by any component).
    // The modal still works — the editor can upload a URL for the
    // slot, exactly like clicking "Edit" from /admin/images does.
    // Called once when the polling loop exits (with or without a hit).
    let editorTriggered = false;
    const triggerEditor = (el) => {
      if (editorTriggered) return;
      editorTriggered = true;
      try {
        const currentUrl = (el && (el.currentSrc || el.getAttribute("src"))) || "";
        const alt        = (el && el.getAttribute && el.getAttribute("alt")) || "";
        // `resolved: true` tells AutoImageEditor that the DOM lookup
        // is complete — the editor can open immediately even when we
        // never found a matching <img> (legacy slot with no live
        // renderer). No point letting AutoImageEditor re-poll another
        // 7s over the same absent element.
        openImageEditor(slot, { currentUrl, alt, resolved: true });
      } catch (_e) { /* ignore — context may not be available in isolated test envs */ }
    };

    const tryHighlight = () => {
      if (cancelled) return;
      attempts += 1;
      const el = findSlotElement(slot);
      if (!el) {
        if (attempts < MAX_ATTEMPTS) {
          window.setTimeout(tryHighlight, 250);
        } else {
          // Element never appeared — still open the editor so the
          // admin can edit the slot regardless (matches the /admin/images
          // Edit button behaviour). No scroll / highlight to do.
          triggerEditor(null);
        }
        return;
      }
      // Scroll into view — offset a bit above centre so the top of the
      // image is visible even with the fixed header.
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (_e) {
        el.scrollIntoView();
      }
      el.classList.add(HIGHLIGHT_CLASS);
      // Auto-clear the ring after the animation finishes so it doesn't
      // permanently distract from the rest of the page.
      window.setTimeout(() => {
        try { el.classList.remove(HIGHLIGHT_CLASS); } catch (_e) { /* ignore */ }
      }, 6000);

      // "Zoom Directo Slot" — hand off to the CMS editor so the admin
      // lands directly in the ImageEditModal for this slot. currentUrl
      // + alt are captured from the resolved <img> so the modal shows
      // the exact live override.
      triggerEditor(el);
    };

    // First attempt after the initial paint; then poll for lazy content.
    const t = window.setTimeout(tryHighlight, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [location.pathname, location.search, openImageEditor]);

  return null;
};

export default PreviewHighlighter;
